import { useState } from "react";
import axios from "axios";
import {
  Activity, CalendarRange, CheckCircle2, Clock, FileJson, Layers,
  ListChecks, Lock, Power, RefreshCw, RotateCcw, Server, Users,
  XCircle, Zap, AlertTriangle, Loader2,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { API } from "@/modules/collector_v2/lib/api";
import { formatNumber, formatRelativeSeconds } from "@/modules/collector_v2/lib/formatters";
import { useApiPolling } from "@/modules/collector_v2/hooks/useApiPolling";
import { PageHeader } from "@/modules/collector_v2/components/common/PageHeader";
import { LoadingState } from "@/modules/collector_v2/components/common/LoadingState";
import { ErrorState } from "@/modules/collector_v2/components/common/ErrorState";
import { CollectorControlButtons } from "@/modules/collector_v2/components/collector/CollectorControlButtons";
import { RdkeyRefreshButton } from "@/modules/collector_v2/components/collector/RdkeyRefreshButton";
import { MacAgentCard } from "@/modules/collector_v2/components/collector/MacAgentCard";

// ===== Helpers visuales =====
function StatRow({ label, value, mono, danger }) {
  return (
    <div className="flex justify-between items-center py-2 px-3 rounded bg-gray-50 text-sm">
      <span className="text-gray-600">{label}</span>
      <span className={`font-semibold tabular-nums ${mono ? "font-mono text-xs" : ""} ${danger ? "text-red-600" : "text-gray-900"}`}>{value}</span>
    </div>
  );
}

function KpiMini({ title, value, sub, icon: Icon, color = "#0891b2", bg = "#ecfeff", action }) {
  return (
    <Card className="relative">
      <CardContent className="flex items-center gap-3 p-4">
        <div style={{ width: 38, height: 38, borderRadius: 9, background: bg, color, display: "grid", placeItems: "center", flexShrink: 0 }}>
          <Icon style={{ width: 17, height: 17 }} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-gray-500">{title}</p>
          <p className="text-xl font-bold tabular-nums" style={{ lineHeight: 1.2 }}>{value}</p>
          {sub && <p className="text-[11px] text-gray-400 truncate">{sub}</p>}
        </div>
        {action && (
          <div className="absolute top-2 right-2">{action}</div>
        )}
      </CardContent>
    </Card>
  );
}

// Sub-componente: KPI "Esta semana" con botón reset.
function WeekCounterCard() {
  const weekApi = useApiPolling("/v2/collector/week-counter", 5000);
  const [busy, setBusy] = useState(false);
  const data = weekApi.data || {};
  const value = formatNumber(data.hands_since ?? 0);
  let sub = "Sin semana iniciada";
  if (data.week_start_at) {
    try {
      const d = new Date(data.week_start_at);
      sub = "Desde " + d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" }) +
            " · " + d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
    } catch { sub = "Desde " + data.week_start_at; }
  }
  const handleReset = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/v2/collector/week-counter/reset`, {});
      toast.success("Semana reiniciada", {
        description: "Contador en 0 desde ahora. Las manos en BD no se borraron.",
      });
      weekApi.refetch?.();
    } catch (e) {
      toast.error("No se pudo reiniciar", { description: String(e.message || e) });
    } finally {
      setBusy(false);
    }
  };
  const resetBtn = (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          data-testid="week-counter-reset-btn"
          title="Reiniciar contador semanal"
          className="h-7 w-7 rounded-md grid place-items-center text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
          disabled={busy}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="week-counter-reset-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>¿Reiniciar contador semanal?</AlertDialogTitle>
          <AlertDialogDescription>
            Esto marca el inicio de una nueva semana de captura. <strong>No borra manos</strong> de
            la base de datos — solo reinicia el contador visible en esta tarjeta. Útil cuando se
            cierra el ciclo del club y querés un nuevo agregado desde cero.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="week-counter-reset-cancel">Cancelar</AlertDialogCancel>
          <AlertDialogAction data-testid="week-counter-reset-confirm" onClick={handleReset}>
            Reiniciar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  return (
    <KpiMini
      title="Desde último reinicio"
      value={value}
      sub={sub}
      icon={CalendarRange}
      color="#0e7490"
      bg="#cffafe"
      action={resetBtn}
    />
  );
}

// ===== Página principal =====
export default function V2CollectorPage() {
  const statusApi = useApiPolling("/v2/collector/status", 5000);
  const metricsApi = useApiPolling("/v2/collector/metrics/live", 5000);
  const registryApi = useApiPolling("/v2/registry/overview", 8000);
  const lobbyApi = useApiPolling("/v2/collector/lobby/snapshot", 5000);

  const status = statusApi.data;
  const metrics = metricsApi.data;
  const registry = registryApi.data;
  const lobby = lobbyApi.data;
  const loading = statusApi.loading || metricsApi.loading;
  const error = statusApi.error || metricsApi.error;

  const refresh = () => { statusApi.refetch(); metricsApi.refetch(); registryApi.refetch(); lobbyApi.refetch(); };

  const multiAccount = status?.multi_account || {};
  const isMulti = multiAccount.mode === "multi_account";
  const accounts = multiAccount.accounts || [];
  const conn = status?.connection_stats || {};
  const worker = status?.worker_stats || {};
  const tableStats = status?.table_stats || {};
  const regKpis = registry?.kpis || {};

  // ----- Agregados para modo multi-account -----
  // Cuando hay multiAccount, los stats del root (worker/table) reflejan solo una cuenta.
  // Sumamos desde accounts[] para obtener métricas reales del enjambre.
  const aggTablesAssigned = accounts.reduce((s, a) => s + (a.tables_assigned ?? 0), 0);
  const aggHandsCollected = accounts.reduce((s, a) => s + (a.hands_collected ?? 0), 0);
  const aggErrors = accounts.reduce((s, a) => s + (a.errors ?? 0), 0);
  const anyConnected = accounts.some((a) => a.is_connected);
  const allConnected = accounts.length > 0 && accounts.every((a) => a.is_connected);

  // Métricas a mostrar (multi-account => suma de cuentas; single => stats del root)
  const showMesasAsignadas = isMulti ? aggTablesAssigned : (worker.last_cycle_tables_scanned ?? 0);
  const showManosTotal = isMulti ? aggHandsCollected : (worker.total_hands_collected ?? 0);
  const showErroresTotal = isMulti ? aggErrors : ((worker.total_errors ?? 0) + (conn.total_errors ?? 0));

  return (
    <>
      <PageHeader
        eyebrow="Centro de Control"
        title="Collector V2"
        description="Estado en tiempo real del escaner de mesas PPPoker."
        action={
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]"><RefreshCw className="h-3 w-3 mr-1" /> Auto 5s</Badge>
            <Button variant="outline" size="sm" onClick={refresh}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refrescar</Button>
          </div>
        }
      />

      {loading && !status && <LoadingState />}
      {error && !status && <ErrorState message={error} onRetry={refresh} />}

      {status && (
        <div className="space-y-4" data-testid="collector-v2-page">

          <div className="text-sm text-slate-500" role="status">
            {status?.controller?.stop_pending ? "Deteniendo Collector: esperando el cierre seguro de la conexión. " : ""}
            {status?.controller?.stop_error ? "La parada requiere revisión; no se permite otro arranque. " : ""}
            {metrics?.as_of ? `Estadísticas calculadas: ${new Date(metrics.as_of).toLocaleString()}. Se actualizan cada minuto.` : "Preparando estadísticas históricas…"}
            {(metrics?.metrics_stale || metricsApi.error) && " Actualización pendiente; se conserva la última lectura disponible."}
          </div>
          <MacAgentCard onSessionCaptured={refresh} />

          {/* BARRA DE ESTADO + CONTROLES */}
          <Card data-testid="collector-status-bar">
            <CardContent className="flex items-center gap-4 py-4 flex-wrap">
              <div
                className="rounded-lg flex items-center justify-center"
                style={{
                  width: 48, height: 48, flexShrink: 0,
                  background: status.is_stopped ? "#fef2f2" : "#ecfdf5",
                  color: status.is_stopped ? "#dc2626" : "#059669",
                }}
              >
                {status.is_stopped ? <Power style={{ width: 22, height: 22 }} /> : <Activity style={{ width: 22, height: 22 }} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge
                    data-testid="collector-runtime-badge"
                    style={{
                      background: status.runtime_state === "running" ? "#dcfce7" : status.runtime_state === "stopping" ? "#fef3c7" : "#fee2e2",
                      color: status.runtime_state === "running" ? "#166534" : status.runtime_state === "stopping" ? "#92400e" : "#991b1b",
                      border: "none",
                    }}
                  >
                    {status.runtime_state?.toUpperCase() || "UNKNOWN"}
                  </Badge>
                  {status.safety_lock && (
                    <Badge variant="outline" className="text-xs text-amber-600 border-amber-300 bg-amber-50">
                      <Lock className="h-3 w-3 mr-1" /> SAFETY LOCK
                    </Badge>
                  )}
                  <Badge variant="outline" className="text-[10px]">
                    {isMulti ? "Multi-Account" : "Single Account"}
                    {isMulti && ` (${multiAccount.active_accounts}/${multiAccount.total_accounts})`}
                  </Badge>
                </div>
                <p className="text-sm font-semibold mt-1">
                  {status.is_stopped ? "Collector detenido" : "Collector activo — escaneando mesas"}
                </p>
                <p className="text-xs text-gray-400">{status.safety_note}</p>
              </div>
              <CollectorControlButtons status={status} onChange={refresh} />
            </CardContent>
          </Card>

          {/* KPIs (datos agregados en multi-account) */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <KpiMini
              title="Manos históricas"
              value={formatNumber(metrics?.hands?.total ?? 0)}
              sub={metrics ? (metrics.hands.last_24h + " jugadas en 24h / " + metrics.hands.last_1h + " jugadas en 1h") : ""}
              icon={FileJson}
            />
            <KpiMini
              title="Mesas históricas"
              value={formatNumber(metrics?.tables?.unique_total ?? 0)}
              sub={metrics ? (metrics.tables.unique_last_24h + " con manos jugadas en 24h") : ""}
              icon={Layers} color="#7c3aed" bg="#faf5ff"
            />
            <KpiMini
              title="Ciclos de esta ejecución"
              value={formatNumber(worker.total_cycles ?? 0)}
              sub={worker.last_cycle_success === false ? "Ultimo ciclo FALLO" : (worker.last_cycle_seconds ? (worker.last_cycle_seconds.toFixed(0) + "s ultimo ciclo") : (isMulti ? `${accounts.length} workers en paralelo` : ""))}
              icon={RefreshCw} color="#ea580c" bg="#fff7ed"
            />
            <WeekCounterCard />
            <KpiMini
              title="Incidencias de conexión / ciclo"
              value={formatNumber(showErroresTotal)}
              sub={isMulti ? "Suma de incidencias por cuenta" : `Transporte: ${conn.total_errors ?? 0} · Ciclo: ${worker.total_errors ?? 0}`}
              icon={AlertTriangle} color={showErroresTotal > 10 ? "#dc2626" : "#059669"} bg={showErroresTotal > 10 ? "#fef2f2" : "#ecfdf5"}
            />
          </div>

          {/* ⭐ LOBBY HEALTH — discover_lobby_tables() telemetry */}
          <Card data-testid="collector-lobby-health-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Layers className="h-4 w-4 text-cyan-600" />
                Salud del Lobby PPPoker
                <Badge variant="outline" className="text-[10px] ml-auto">auto-refresh 5s</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const agg = lobby?.aggregate || {};
                const hasData = lobby?.has_data === true;
                const serverTotal = agg.server_total_active ?? 0;
                const received = agg.tables_received ?? 0;
                const twoPlus = agg.tables_with_2plus ?? 0;
                const onePlayer = agg.tables_with_1 ?? 0;
                const empty = agg.tables_empty ?? 0;
                const scanning = agg.tables_being_scanned ?? 0;
                const missing = Math.max(0, serverTotal - received);
                const coverage = serverTotal > 0 ? Math.round((received / serverTotal) * 100) : (received > 0 ? 100 : 0);
                const scanRatio = twoPlus > 0 ? Math.round((scanning / twoPlus) * 100) : 0;
                return (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="collector-lobby-kpis">
                      <KpiMini
                        title="Mesas en lobby (server)"
                        value={hasData ? formatNumber(serverTotal) : "—"}
                        sub={hasData ? `Recibidas: ${formatNumber(received)} · ${new Date(lobby.snapshot_at).toLocaleTimeString("es-AR")}` : "Sin snapshot disponible"}
                        icon={Server}
                        color={coverage >= 95 ? "#059669" : coverage >= 70 ? "#d97706" : "#dc2626"}
                        bg={coverage >= 95 ? "#ecfdf5" : coverage >= 70 ? "#fffbeb" : "#fef2f2"}
                      />
                      <KpiMini
                        title="Mesas con ≥2 jugadores"
                        value={hasData ? formatNumber(twoPlus) : "—"}
                        sub={onePlayer > 0 ? `+${onePlayer} con 1 jugador (stand-by)` : "Antes de excluir modalidades"}
                        icon={Users}
                        color="#0891b2"
                        bg="#ecfeff"
                      />
                      <KpiMini
                        title="Escaneando ahora"
                        value={formatNumber(isMulti ? scanning : (tableStats.open_tables_count ?? scanning))}
                        sub={twoPlus > 0 ? `${scanRatio}% del set activo` : "Sin leases activos"}
                        icon={Activity}
                        color="#7c3aed"
                        bg="#faf5ff"
                      />
                      <KpiMini
                        title={missing > 0 ? "Mesas no recolectadas" : "Cobertura"}
                        value={!hasData || serverTotal === 0 ? "—" : missing > 0 ? formatNumber(missing) : `${coverage}%`}
                        sub={!hasData ? "Sin datos para calcular cobertura" : serverTotal === 0 ? "Último snapshot sin mesas" : missing > 0 ? "Diferencia respecto del snapshot" : "Todas las mesas del snapshot recibidas"}
                        icon={missing > 0 ? AlertTriangle : CheckCircle2}
                        color={missing > 0 ? "#dc2626" : "#059669"}
                        bg={missing > 0 ? "#fef2f2" : "#ecfdf5"}
                      />
                    </div>
                    {/* Detalle por scout */}
                    {Array.isArray(lobby?.per_scout) && lobby.per_scout.length > 0 && (
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="collector-lobby-per-scout">
                        {lobby.per_scout.map((s) => {
                          const ll = s.last_lobby || {};
                          const isWorker = ll.role === "worker" || ll.skipped === true;
                          const isDiscoverer = lobby?.discoverer && s.account === lobby.discoverer;
                          const isHealthy = (ll.server_total_active ?? 0) > 0;
                          const badgeLabel = isWorker
                            ? "Worker"
                            : isDiscoverer
                              ? (isHealthy ? "Discoverer" : "0 mesas")
                              : (isHealthy ? "OK" : "0 mesas");
                          const badgeBg = isWorker
                            ? "#e0e7ff"
                            : isDiscoverer && isHealthy
                              ? "#cffafe"
                              : isHealthy
                                ? "#dcfce7"
                                : "#fef2f2";
                          const badgeFg = isWorker
                            ? "#3730a3"
                            : isDiscoverer && isHealthy
                              ? "#155e75"
                              : isHealthy
                                ? "#166534"
                                : "#991b1b";
                          return (
                            <div key={s.account}
                                 className="rounded-md border px-3 py-2 text-xs bg-gray-50"
                                 data-testid={`lobby-scout-${s.account}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-semibold capitalize">{s.account}</span>
                                <Badge
                                  variant="outline"
                                  className="text-[9px]"
                                  style={{
                                    background: badgeBg,
                                    color: badgeFg,
                                    border: "none",
                                  }}
                                  data-testid={`lobby-scout-badge-${s.account}`}
                                >
                                  {badgeLabel}
                                </Badge>
                              </div>
                              <div className="text-gray-500">
                                UID <span className="font-mono">{s.scout_uid || "—"}</span>
                              </div>
                              {isWorker ? (
                                <div className="text-[10px] text-gray-400 mt-1 italic">
                                  Recibe targets pre-asignados del discoverer
                                </div>
                              ) : (
                                <>
                                  <div className="flex justify-between mt-1">
                                    <span className="text-gray-500">Lobby</span>
                                    <span className="font-mono">{ll.server_total_active ?? 0} / rcvd {ll.tables_received ?? 0}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">≥2 jug.</span>
                                    <span className="font-mono">{ll.tables_with_2plus ?? 0}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Páginas</span>
                                    <span className="font-mono">{ll.pages_fetched ?? 0}</span>
                                  </div>
                                  {ll.stop_reason && (
                                    <div className="mt-1 text-[10px] text-gray-400 truncate" title={ll.stop_reason}>
                                      stop: {ll.stop_reason}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>

          {/* CUENTAS + ACTIVIDAD */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

            {/* Tarjeta: Conexión PPPoker / Cuentas activas */}
            <Card data-testid="collector-accounts-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Server className="h-4 w-4" />
                  {isMulti ? "Conexión PPPoker — Cuentas" : "Conexión PPPoker"}
                  {isMulti && (
                    <Badge
                      variant="outline"
                      className="text-[10px]"
                      style={{
                        background: allConnected ? "#f0fdf4" : (anyConnected ? "#fffbeb" : "#fef2f2"),
                        color: allConnected ? "#166534" : (anyConnected ? "#92400e" : "#991b1b"),
                        borderColor: allConnected ? "#bbf7d0" : (anyConnected ? "#fde68a" : "#fecaca"),
                      }}
                    >
                      {multiAccount.active_accounts}/{multiAccount.total_accounts} conectadas
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {isMulti && accounts.length > 0 ? (
                  <>
                    {accounts.map((acc) => (
                      <div
                        key={acc.name}
                        data-testid={"account-row-" + acc.name}
                        className="flex items-center gap-3 py-2.5 px-3 rounded-lg border"
                        style={{
                          background: acc.is_connected ? "#f0fdf4" : "#fef2f2",
                          borderColor: acc.is_connected ? "#bbf7d0" : "#fecaca",
                        }}
                      >
                        <div
                          className="rounded-full flex items-center justify-center"
                          style={{ width: 30, height: 30, background: acc.is_connected ? "#dcfce7" : "#fee2e2" }}
                        >
                          {acc.is_connected ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4 text-red-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold">{acc.name}</p>
                            <Badge
                              className="text-[9px]"
                              style={{
                                background: acc.is_connected ? "#dcfce7" : "#fee2e2",
                                color: acc.is_connected ? "#166534" : "#991b1b",
                                border: "none",
                              }}
                            >
                              {acc.is_connected ? "ONLINE" : "OFFLINE"}
                            </Badge>
                            {!acc.is_active && (
                              <Badge variant="outline" className="text-[9px] text-amber-700 border-amber-300 bg-amber-50">
                                pausada
                              </Badge>
                            )}
                          </div>
                          <p className="text-[11px] text-gray-500 font-mono">UID {acc.uid}</p>
                          {acc.last_error && (
                            <p className="text-[10px] text-red-600 truncate mt-0.5" title={acc.last_error}>
                              ⚠ {acc.last_error}
                            </p>
                          )}
                        </div>
                        <div className="text-right text-xs">
                          <p className="text-gray-700 tabular-nums font-semibold">
                            {formatNumber(acc.tables_assigned)} <span className="text-gray-400 font-normal">mesas</span>
                          </p>
                          <p className="text-gray-500 tabular-nums">
                            {formatNumber(acc.hands_collected)} <span className="text-gray-400">manos</span>
                          </p>
                          {acc.errors > 0 && (
                            <Badge variant="destructive" className="text-[9px] mt-0.5">{acc.errors} err</Badge>
                          )}
                          <RdkeyRefreshButton accountName={acc.name} uid={acc.uid} onSuccess={refresh} />
                        </div>
                      </div>
                    ))}
                    {/* Datos comunes a todas las cuentas */}
                    <div className="pt-2 mt-1 border-t border-gray-100 grid grid-cols-2 gap-1.5">
                      <StatRow label="Credencial (estimación local)" value={conn.rdkey_valid ? "Disponible según control local" : "Requiere verificación"} danger={!conn.rdkey_valid} />
                      <StatRow label="Tiempo desde login" value={formatRelativeSeconds(conn.rdkey_age_seconds)} />
                    </div>
                  </>
                ) : (
                  <>
                    <StatRow label="Último estado del socket" value={
                      <Badge style={{ background: conn.state === "connected" || conn.state === "authenticated" ? "#dcfce7" : "#fee2e2", color: conn.state === "connected" || conn.state === "authenticated" ? "#166534" : "#991b1b", border: "none", fontSize: 11 }}>{conn.state || "?"}</Badge>
                    } />
                    <StatRow label="Credencial (estimación local)" value={conn.rdkey_valid ? "Disponible según control local" : "Requiere verificación"} danger={!conn.rdkey_valid} />
                    <StatRow label="Tiempo desde login" value={formatRelativeSeconds(conn.rdkey_age_seconds)} />
                    <StatRow label="Servidor" value={(status.config_snapshot?.server || "?") + ":" + (status.config_snapshot?.port || "?")} mono />
                    <StatRow label="UID / Club" value={(status.config_snapshot?.uid || "?") + " / " + (status.config_snapshot?.club_id || "?")} mono />
                  </>
                )}
                <div className="pt-1.5 text-[11px] text-gray-400">
                  Servidor: {status.config_snapshot?.server || "?"}:{status.config_snapshot?.port || "?"} · Club: {status.config_snapshot?.club_id || "?"}
                </div>
              </CardContent>
            </Card>

            {/* Tarjeta: Actividad / Último ciclo */}
            <Card data-testid="collector-activity-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  {isMulti ? "Último ciclo — Agregado" : "Actividad del ciclo"}
                  {isMulti && (
                    <Badge variant="outline" className="text-[10px] ml-auto">
                      {accounts.length} workers
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                <StatRow label="Estado" value={
                  status.runtime_state === "running" && (isMulti ? anyConnected : true)
                    ? <Badge style={{ background: "#dcfce7", color: "#166534", border: "none", fontSize: 11 }}>Escaneando</Badge>
                    : status.is_stopped
                      ? <Badge style={{ background: "#fee2e2", color: "#991b1b", border: "none", fontSize: 11 }}>Detenido</Badge>
                      : <Badge variant="outline" className="text-[10px]">{status.runtime_state}</Badge>
                } />

                {isMulti ? (
                  <>
                    {/* Detalle por cuenta */}
                    {accounts.map((acc) => {
                      const enterRate = Math.round((acc.enter_success_rate ?? 1) * 100);
                      const leaveRate = Math.round((acc.leave_verification_rate ?? 1) * 100);
                      const leaveOk = (acc.total_leaves ?? 0) - (acc.total_leave_not_verified ?? 0);
                      return (
                        <div
                          key={"act-" + acc.name}
                          data-testid={"activity-row-" + acc.name}
                          className="rounded-lg border px-3 py-2"
                          style={{
                            background: acc.is_connected ? "#f9fafb" : "#fef2f2",
                            borderColor: acc.is_connected ? "#e5e7eb" : "#fecaca",
                          }}
                        >
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-2 min-w-0">
                              <span
                                className="rounded-full flex-shrink-0"
                                style={{ width: 8, height: 8, background: acc.is_connected ? "#16a34a" : "#dc2626" }}
                              />
                              <span className="text-sm font-semibold text-gray-800">{acc.name}</span>
                              {(Array.isArray(acc.current_table_ids) && acc.current_table_ids.length > 0) ? (
                                <div className="flex items-center gap-1 flex-wrap">
                                  {acc.current_table_ids.map((tid) => (
                                    <Badge
                                      key={"mt-" + acc.name + "-" + tid}
                                      data-testid={"open-table-badge-" + acc.name + "-" + tid}
                                      style={{ background: "#dcfce7", color: "#166534", border: "none", fontSize: 10 }}
                                    >
                                      Mesa {tid}
                                    </Badge>
                                  ))}
                                  {(acc.max_concurrent_tables ?? 1) > 1 && (
                                    <span className="text-[10px] text-gray-400 tabular-nums" data-testid={"open-table-count-" + acc.name}>
                                      ({acc.current_table_ids.length}/{acc.max_concurrent_tables})
                                    </span>
                                  )}
                                </div>
                              ) : acc.is_in_table ? (
                                <Badge style={{ background: "#dcfce7", color: "#166534", border: "none", fontSize: 10 }} data-testid={"open-table-badge-" + acc.name + "-" + acc.current_table_id}>
                                  Mesa {acc.current_table_id}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] text-gray-500" data-testid={"transit-badge-" + acc.name}>
                                  Buscando mesa…
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-[11px] tabular-nums text-gray-500">
                              <span>
                                <span className="font-semibold text-gray-800">{formatNumber(acc.tables_assigned)}</span> mesas
                              </span>
                              <span>·</span>
                              <span>
                                <span className="font-semibold text-gray-800">{formatNumber(acc.hands_collected)}</span> manos
                              </span>
                              {acc.errors > 0 && (
                                <Badge variant="destructive" className="text-[9px] ml-1">{acc.errors}</Badge>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-1.5">
                            <div className="text-[11px] flex justify-between bg-white rounded px-2 py-1">
                              <span className="text-gray-500">Enter exitosos</span>
                              <span className="font-semibold tabular-nums">
                                {formatNumber(acc.total_enters ?? 0)}
                                {(acc.total_enters ?? 0) > 0 && (
                                  <span className={`ml-1 ${enterRate >= 90 ? "text-green-600" : enterRate >= 70 ? "text-amber-600" : "text-red-600"}`}>
                                    ({enterRate}%)
                                  </span>
                                )}
                              </span>
                            </div>
                            <div className="text-[11px] flex justify-between bg-white rounded px-2 py-1">
                              <span className="text-gray-500">Leave verificados</span>
                              <span className="font-semibold tabular-nums">
                                {formatNumber(leaveOk)} / {formatNumber(acc.total_leaves ?? 0)}
                                {(acc.total_leaves ?? 0) > 0 && (
                                  <span className={`ml-1 ${leaveRate >= 90 ? "text-green-600" : leaveRate >= 70 ? "text-amber-600" : "text-red-600"}`}>
                                    ({leaveRate}%)
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                          {(acc.total_leave_not_verified ?? 0) > 0 && (
                            <p className="mt-1 text-[10px] text-red-600">
                              ⚠ {acc.total_leave_not_verified} leaves NO verificados último ciclo
                            </p>
                          )}
                        </div>
                      );
                    })}
                    {/* Totales agregados */}
                    <div className="pt-2 mt-1 border-t border-gray-100 space-y-1.5">
                      <StatRow
                        label="Mesas asignadas (total)"
                        value={formatNumber(showMesasAsignadas) + (worker.last_cycle_tables_scanned ? ` · último ciclo: ${formatNumber(worker.last_cycle_tables_scanned)}` : "")}
                      />
                      <StatRow
                        label="Manos recolectadas (total)"
                        value={formatNumber(showManosTotal)}
                      />
                      {worker.last_cycle_seconds > 0 && (
                        <StatRow
                          label="Duración último ciclo"
                          value={worker.last_cycle_seconds.toFixed(1) + "s"}
                        />
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <StatRow label="En mesa" value={
                      tableStats.is_in_table
                        ? <Badge style={{ background: "#dcfce7", color: "#166534", border: "none", fontSize: 11 }}>Mesa {tableStats.current_table_id}</Badge>
                        : <Badge variant="outline" className="text-[10px]">{status.is_stopped ? "Sin mesa activa" : "Buscando mesa…"}</Badge>
                    } />
                    <StatRow label="Mesas descubiertas (último ciclo)" value={formatNumber(worker.last_cycle_tables_discovered ?? 0)} />
                    <StatRow label="Mesas escaneadas" value={
                      (worker.last_cycle_tables_scanned ?? 0) > 0
                        ? formatNumber(worker.last_cycle_tables_scanned) + " (ciclo completo)"
                        : formatNumber(tableStats.total_enters ?? 0) + " (ciclo en curso)"
                    } />
                    <StatRow label="Manos de esta ejecución" value={formatNumber(
                      worker.total_hands_collected ?? 0
                    )} />
                    <StatRow label="Enter exitosos" value={
                      formatNumber(tableStats.total_enters ?? 0) + " (" +
                      Math.round((tableStats.enter_success_rate || 0) * 100) + "%)"
                    } />
                    <StatRow label="Leave verificados" value={
                      formatNumber((tableStats.total_leaves ?? 0) - (tableStats.total_leave_not_verified ?? 0)) +
                      " / " + formatNumber(tableStats.total_leaves ?? 0) +
                      " (" + Math.round((tableStats.leave_verification_rate || 0) * 100) + "%)"
                    } />
                    {(tableStats.total_leave_not_verified ?? 0) > 0 && (
                      <StatRow label="Leave NO verificados" value={tableStats.total_leave_not_verified} danger />
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* REGISTRY — Cola de prioridad simplificada */}
          {regKpis.total > 0 && (
            <Card data-testid="registry-overview-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ListChecks className="h-4 w-4" /> Cola de mesas
                  <span className="ml-auto text-xs text-gray-400 tabular-nums">{formatNumber(regKpis.total)} mesas tracked</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3">
                  {[
                    { label: "Rescate", value: regKpis.rescate, color: "#dc2626", bg: "#fef2f2" },
                    { label: "Urgente", value: regKpis.urgente, color: "#ea580c", bg: "#fff7ed" },
                    { label: "Normal", value: regKpis.normal, color: "#6b7280", bg: "#f9fafb" },
                    { label: "Live", value: regKpis.live, color: "#0891b2", bg: "#ecfeff" },
                    { label: "Retry", value: regKpis.retry_ghost, color: "#d97706", bg: "#fffbeb" },
                    { label: "Gap total", value: regKpis.total_gap, color: "#7c3aed", bg: "#faf5ff" },
                  ].map((k) => (
                    <div key={k.label} className="rounded-lg p-3 text-center" style={{ background: k.bg }}>
                      <p className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: k.color }}>{k.label}</p>
                      <p className="text-lg font-bold tabular-nums" style={{ color: k.color }}>{formatNumber(k.value ?? 0)}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {[
                    { label: "Activas", value: regKpis.active, ok: true },
                    { label: "Up-to-date", value: regKpis.up_to_date, ok: true },
                    { label: "Discovered", value: regKpis.discovered, ok: true },
                    { label: "Scraping", value: regKpis.scraping, ok: false },
                    { label: "Missing", value: regKpis.missing, ok: false },
                    { label: "Dead", value: regKpis.dead, ok: false },
                  ].map((s) => (
                    <Badge key={s.label} variant="outline" className="text-[10px]" style={{
                      background: s.ok ? "#f0fdf4" : "#fef2f2",
                      color: s.ok ? "#166534" : "#991b1b",
                      borderColor: s.ok ? "#bbf7d0" : "#fecaca",
                    }}>
                      {s.label}: {formatNumber(s.value ?? 0)}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* MODALIDAD */}
          {metrics?.modality?.distribution?.length > 0 && (
            <Card data-testid="collector-modality-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Zap className="h-4 w-4" /> Manos por modalidad
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {metrics.modality.distribution.map((row) => (
                    <div key={row.family} className="rounded-lg bg-gray-50 px-3 py-2 text-center">
                      <p className="text-[10px] uppercase font-semibold text-gray-500">{row.family}</p>
                      <p className="text-lg font-bold tabular-nums">{formatNumber(row.count)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}




        </div>
      )}
    </>
  );
}
